import { createFileRoute } from "@tanstack/react-router";
import { Section, SectionHeader, AccreditationStrip, CTABand } from "@/components/site-blocks";
import { ServiceHeroFloatLayer } from "@/components/service-hero-float-layer";
import heroCommercialRetrofit from "@/assets/commercial-retrofit/hero-commercial-retrofit.webp";
import crsOffice1280 from "@/assets/hero/crs-office-1280.webp";
import { rasterSrc } from "@/generated/raster-images";
import { EnquiryForm } from "@/components/enquiry-form";
import {
 Building2,
 Briefcase,
 Users,
 FileCheck,
 ClipboardList,
 Lightbulb,
 PackageCheck,
 AlertTriangle,
 ArrowRight,
 Award,
 Camera,
 Droplets,
 Flame,
 HandHeart,
 Home,
 Leaf,
 Layers,
 ShieldCheck,
 Sun,
 Wind,
 Wrench,
} from "lucide-react";
import { pageSeo } from "@/lib/seo";
import { withServiceLcpPreload } from "@/lib/service-lcp-preload";
import { SECTOR_PAGE_LINKS } from "@/content/commercial/sectors";
import { COMMERCIAL_LINK_LABELS, COMMERCIAL_URLS } from "@/content/commercial/urls";

const PATH = "/for-trade";

export const Route = createFileRoute("/for-trade")({
 head: () =>
  withServiceLcpPreload(PATH, {
 ...pageSeo({
 path: PATH,
 title: "For Trade, Insurers & Housing Associations | CRS Energy",
 description:
 "CRS delivers remediation, insulation and energy-efficiency programmes at scale for insurers, housing associations, local authorities and main contractors across Wales.",
 }),
 }),
 component: ForTradePage,
});

const FOR_TRADE_FLOAT_IMAGES = [
  { src: heroCommercialRetrofit, slug: "commercial-retrofit" },
  { src: rasterSrc("commercial-solar-pv/hero-commercial-solar"), slug: "commercial-solar" },
  { src: crsOffice1280, slug: "crs-office" },
];

function ForTradePage() {
 return (
 <>
 <section className="relative overflow-hidden">
 <div className="absolute inset-0 z-0">
 <img
 src={heroCommercialRetrofit}
 alt="Coordinated retrofit and commercial energy works on a social-housing estate"
 className="w-full h-full object-cover"
 width={1920}
 height={1280}
 loading="eager"
 decoding="async"
 fetchPriority="high"
 />
 <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/60" />
 <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/20 to-primary/30 md:from-primary/20 md:to-transparent" />
 </div>
 <ServiceHeroFloatLayer images={FOR_TRADE_FLOAT_IMAGES} />
 <div className="container-page relative z-[3] py-20 md:py-28 text-primary-foreground">
 <p className="text-sm font-semibold tracking-wider uppercase text-accent-soft mb-3">
 For trade
 </p>
 <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold max-w-4xl text-balance">
 For insurers, housing associations and local authorities
 </h1>
 <p className="mt-5 text-lg md:text-xl text-primary-foreground/85 max-w-3xl">
 CRS delivers remediation, insulation and energy-efficiency programmes at scale. Single property to portfolio-wide.
 </p>
 </div>
 </section>

 {/* Pressure stats */}
 <Section>
 <SectionHeader
 eyebrow="The pressure on social housing"
 title="Social housing in Wales is under more pressure than ever"
 description="Void properties sit empty for weeks while costs accumulate. Damp and mould complaints return faster than they can be resolved. Repair backlogs grow, tenants escalate to the Public Services Ombudsman for Wales, and the regulatory framework is tightening."
 />
 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
 {[
 {
 stat: "74 days",
 label: "Average void turnaround",
 body: "Every day a property sits empty costs lost rent, council tax, and vandalism risk. Reactive maintenance models are increasingly difficult to sustain.",
 source: "Source: UK social housing sector, 2023/24",
 },
 {
 stat: "+474%",
 label: "Rise in disrepair claims",
 body: "Complaints handled by housing redress bodies have surged. A large share stem from poor practice such as missing records, weak diagnostics, delays and poor QA.",
 source: "Source: UK social housing sector, 2023/24. In Wales, complaints route via the Public Services Ombudsman for Wales.",
 },
 {
 stat: "£3,700+",
 label: "Void cost per unit",
 body: "Reported average repair cost per void property across the sector, indicative of the cost pressure on landlords managing ageing stock.",
 source: "Source: UK social housing sector, 2023/24",
 },
 {
 stat: "£9bn",
 label: "Record repairs spend",
 body: "Social landlords spent a record amount on repairs and maintenance in 2023/24. The reactive maintenance model is increasingly difficult to sustain.",
 source: "Source: UK social housing sector, 2023/24",
 },
 ].map((c) => (
 <div key={c.stat} className="rounded-2xl border bg-card p-6 flex flex-col">
 <div className="text-3xl font-bold text-accent">{c.stat}</div>
 <div className="mt-1 text-sm font-semibold text-primary">{c.label}</div>
 <p className="mt-3 text-sm text-muted-foreground flex-1">{c.body}</p>
 <p className="mt-3 text-[11px] text-muted-foreground/80 italic">{c.source}</p>
 </div>
 ))}
 </div>
 </Section>

 {/* Regulation */}
 <Section className="bg-card">
 <div className="grid lg:grid-cols-2 gap-10 items-start">
 <div>
 <SectionHeader
 eyebrow="Regulation"
 title="The regulatory landscape is tightening"
 description="Under the Renting Homes (Wales) Act 2016, every dwelling let on an occupation contract (under seven years) must be fit for human habitation, judged against 29 matters and circumstances. While a home is unfit, rent is not payable for each day it remains so, and tenants can pursue damages through the courts."
 />
 </div>
 <div className="rounded-2xl border bg-background p-6 space-y-5">
 <div className="flex items-start gap-3">
 <AlertTriangle className="h-6 w-6 text-accent flex-shrink-0 mt-1" aria-hidden />
 <div>
 <h3 className="font-semibold text-primary">WHQS hazard response rule, from 1 April 2026</h3>
 <p className="mt-1 text-sm text-muted-foreground">
 Social landlords must investigate and remedy hazards, including damp and mould, within set timescales. For hazards with a likelihood of imminent harm: investigate within 24 hours and remedy within a further 24 hours. (Welsh Government, December 2025.)
 </p>
 </div>
 </div>
 <div className="flex items-start gap-3">
 <FileCheck className="h-6 w-6 text-accent flex-shrink-0 mt-1" aria-hidden />
 <div>
 <h3 className="font-semibold text-primary">Compliance reporting</h3>
 <p className="mt-1 text-sm text-muted-foreground">
 Mandatory reporting to Welsh Government on hazard response performance is being introduced alongside the new rule.
 </p>
 </div>
 </div>
 </div>
 </div>
 </Section>

 {/* B) What's driving your programme */}
 <Section>
 <SectionHeader
 eyebrow="Connective tissue"
 title="What's driving your programme"
 description="Each regulatory driver maps to a CRS delivery response. The relevant service page carries the commercial / social-housing detail."
 />
 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
 {[
 {
 icon: AlertTriangle,
 driver: "WHQS hazard response rule (1 Apr 2026)",
 response: "Damp & mould diagnostics with ventilation & extraction response.",
 links: [
 { label: "Damp & Mould Response", href: "/commercial-damp-mould-ventilation" },
 { label: "Ventilation & Extraction", href: "/ventilation-extraction" },
 ],
 },
 {
 icon: Home,
 driver: "Fitness for Human Habitation (29 matters)",
 response: "Whole-home remediation across damp, structural and ventilation.",
 links: [
 { label: "Damp & Mould", href: "/damp-survey" },
 { label: "Remedial Wall Ties", href: "/remedial-wall-ties" },
 { label: "Ventilation", href: "/ventilation-extraction" },
 ],
 },
 {
 icon: Leaf,
 driver: "WHQS 2023 decarbonisation & energy targets",
 response: "Fabric-first retrofit plus low-carbon heat and generation.",
 links: [
 { label: "Loft Insulation", href: "/loft-insulation" },
 { label: "Internal Wall Insulation", href: "/internal-wall-insulation" },
 { label: "Heat Pumps", href: "/air-source-heat-pumps" },
 { label: "Solar PV", href: "/solar-pv" },
 ],
 },
 {
 icon: ClipboardList,
 driver: "Void turnaround & disrepair backlogs",
 response: "Single-programme remediation delivery against agreed SLAs.",
 links: [
 { label: "Talk To Our Team", href: "#enquiry" },
 ],
 },
 ].map((c) => {
 const Icon = c.icon;
 return (
 <div key={c.driver} className="rounded-2xl border bg-card p-6 flex flex-col">
 <div className="rounded-xl bg-accent/10 text-accent p-3 w-fit">
 <Icon className="h-6 w-6" aria-hidden />
 </div>
 <h3 className="mt-3 text-base font-semibold text-primary">{c.driver}</h3>
 <p className="mt-2 text-sm text-muted-foreground flex-1">{c.response}</p>
 <div className="mt-4 flex flex-wrap gap-1.5">
 {c.links.map((l) => (
 <a
 key={l.href + l.label}
 href={l.href}
 className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-semibold text-primary hover:text-accent hover:border-accent transition-colors"
 >
 {l.label}
 <ArrowRight className="h-3 w-3" aria-hidden />
 </a>
 ))}
 </div>
 </div>
 );
 })}
 </div>
 </Section>

 {/* Delivery model: 7 days softened */}
 <Section className="bg-card">
 <SectionHeader
 eyebrow="Our delivery model"
 title="Rapid, SLA-backed turnaround"
 description="CRS Energy works with housing associations and local authorities across Wales to clear property backlogs, resolve longstanding defects and bring homes up to standard against a target of 7 days on agreed works. Rather than managing multiple contractors across fragmented programmes, you work with a single delivery partner taking responsibility from initial assessment through to completion and sign-off."
 />
 <div className="grid md:grid-cols-3 gap-5">
 {[
 { icon: ClipboardList, step: "01", title: "You share your data", body: "Send us your stock data, priority properties, and the issues you need resolved." },
 { icon: Lightbulb, step: "02", title: "We scope & cost", body: "We return a costed programme with a clear timeline, covering all required works." },
 { icon: PackageCheck, step: "03", title: "We deliver to SLA", body: "Works are completed against a target of 7 days on agreed scope. Delivered to the SLA agreed in your programme." },
 ].map((c) => {
 const Icon = c.icon;
 return (
 <div key={c.step} className="rounded-2xl border bg-background p-6">
 <div className="flex items-center justify-between">
 <div className="rounded-xl bg-accent/10 text-accent p-3 w-fit">
 <Icon className="h-6 w-6" aria-hidden />
 </div>
 <span className="text-sm font-bold text-muted-foreground">{c.step}</span>
 </div>
 <h3 className="mt-3 text-lg font-semibold">{c.title}</h3>
 <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
 </div>
 );
 })}
 </div>
 </Section>

 {/* Who we work with */}
 <Section>
 <SectionHeader eyebrow="Who we work with" title="Trusted across the supply chain" />
 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
 {[
 { icon: Briefcase, title: "Insurers", body: "Damp, mould and structural claims handled with full reporting and post-works verification." },
 { icon: Building2, title: "Housing associations", body: "Planned and reactive maintenance across managed stock, including void turnaround." },
 { icon: Users, title: "Local authorities", body: "LA Flex, Warm Homes and broader retrofit programme delivery." },
 { icon: FileCheck, title: "Main contractors", body: "Specialist sub-contract packages for cavity removal, IWI, wall ties and ventilation." },
 ].map((c) => {
 const Icon = c.icon;
 return (
 <div key={c.title} className="rounded-2xl border bg-card p-6">
 <div className="rounded-xl bg-primary-soft text-primary p-3 w-fit">
 <Icon className="h-6 w-6" aria-hidden />
 </div>
 <h3 className="mt-3 text-lg font-semibold">{c.title}</h3>
 <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
 </div>
 );
 })}
 </div>
 </Section>

 {/* B2) Sectors we work with */}
 <Section>
 <SectionHeader
 eyebrow="Sectors we work with"
 title="Buyer-fit delivery for framework and tender procurement"
 description="Shallow sector pages for procurement teams, each links through to the relevant measure pages for technical detail."
 />
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
 {SECTOR_PAGE_LINKS.map((s) => (
 <a
 key={s.slug}
 href={s.path}
 className="rounded-2xl border bg-card p-5 hover:border-accent/40 transition-colors flex flex-col"
 >
 <h3 className="text-base font-semibold text-primary">{s.name}</h3>
 <span className="mt-3 text-sm font-semibold text-accent inline-flex items-center gap-1">
 View sector <ArrowRight className="h-3.5 w-3.5" aria-hidden />
 </span>
 </a>
 ))}
 </div>
 </Section>

 {/* C) Programmes we deliver */}
 <Section className="bg-card">
 <SectionHeader
 eyebrow="Programmes we deliver"
 title="Coordinated programmes across the CRS service set"
 description="Each programme links into the commercial / social-housing layer of the relevant service page, with accreditation framing appropriate to that work type."
 />


 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
 {[
 {
 icon: Droplets,
 title: "Damp, mould & ventilation response",
 scope: "Inspection-led damp, mould and whole-house ventilation response. Documented to hazard-response timescales.",
 accred: "Inspection-Led · PAS 2030 · Insurance-Backed",
 href: "/commercial-damp-mould-ventilation",
 },
 {
 icon: Wind,
 title: "Ventilation & extraction",
 scope: "Whole-house ventilation strategy as part of condensation-and-mould response.",
 accred: "PAS 2030 · Whole-House Assessment",
 href: "/ventilation-extraction",
 },
 {
 icon: Home,
 title: "Loft & internal wall insulation",
 scope: "Retrofit fabric upgrades for EPC uplift and decarbonisation programmes.",
 accred: "PAS 2030 · TrustMark · Insurance-Backed Guarantee",
 href: "/loft-insulation",
 secondHref: { label: "Internal Wall Insulation", href: "/internal-wall-insulation" },
 },
 {
 icon: Wrench,
 title: "Remedial wall ties & structural",
 scope: "Borescope-evidenced tie replacement using tested, CE-marked systems.",
 accred: "Structural · Tested Systems · Fully Insured",
 href: "/commercial-structural",
 secondHref: { label: "Domestic Wall Ties", href: "/remedial-wall-ties" },
 },
 {
 icon: Flame,
 title: "Boilers, heating controls & LGSR",
 scope: "Boiler replacement, controls upgrades, and landlord gas safety (LGSR / CP12).",
 accred: "Gas Safe Registered · Manufacturer-Backed Warranty",
 href: "/boilers-heating",
 secondHref: { label: "Heating Controls", href: "/heating-controls" },
 },
 {
 icon: Leaf,
 title: "Air source heat pumps",
 scope: "Fabric-first heat pump programmes for decarbonisation targets.",
 accred: "MCS Certified · Full Heat-Loss Survey",
 href: "/commercial-heat-pumps",
 },
 {
 icon: Sun,
 title: "Commercial Solar PV",
 scope: "B2B procurement-evidence delivery for commercial estates and HA portfolios.",
 accred: "MCS · Commercial G99 / DNO · Framework-Ready",
 href: "/commercial-solar-pv",
 },
 {
 icon: Layers,
 title: "Insulation Retrofit Programmes",
 scope: "Cavity, loft and IWI for councils, HAs and frameworks, suitability, supervision and conformity records.",
 accred: "PAS 2030 · TrustMark Lodgement · Inspection-Led",
 href: "/commercial-insulation",
 },
 {
 icon: Leaf,
 title: "Whole-House Retrofit & Decarbonisation",
 scope: "PAS 2035 coordinated fabric-first retrofit for councils, HAs and frameworks.",
 accred: "PAS 2035 · TrustMark · TEP-Aligned",
 href: "/commercial-retrofit",
 },
 ].map((p) => {
 const Icon = p.icon;
 return (
 <div key={p.title} className="rounded-2xl border bg-background p-6 flex flex-col">
 <div className="rounded-xl bg-accent/10 text-accent p-3 w-fit">
 <Icon className="h-6 w-6" aria-hidden />
 </div>
 <h3 className="mt-3 text-base font-semibold text-primary">{p.title}</h3>
 <p className="mt-2 text-sm text-muted-foreground flex-1">{p.scope}</p>
 <p className="mt-3 text-[11px] uppercase tracking-wider text-accent font-semibold">{p.accred}</p>
 <div className="mt-4 flex flex-wrap items-center gap-3">
 <a
 href={p.href}
 className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent"
 >
 View programme
 <ArrowRight className="h-3.5 w-3.5" aria-hidden />
 </a>
 {p.secondHref && (
 <a
 href={p.secondHref.href}
 className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent"
 >
 {p.secondHref.label}
 <ArrowRight className="h-3.5 w-3.5" aria-hidden />
 </a>
 )}
 </div>
 </div>
 );
 })}
 </div>
 </Section>

 {/* D) Built for B2B delivery - enhanced with KPIs */}
 <Section>
 <SectionHeader eyebrow="How we work with you" title="Built for B2B delivery" />
 <div className="grid md:grid-cols-3 gap-5">
 {[
 { title: "Single point of contact", body: "Named contract manager from quote through to handover." },
 { title: "Agreed SLAs", body: "Response, attendance and completion times agreed upfront and reported on." },
 {
 title: "Full reporting",
 body: "Programme adherence, H&S, completion / sign-off evidence, warranty registration, and, for retrofit, carbon and social-value reporting. Claims evidenced, not generic.",
 },
 ].map((c) => (
 <div key={c.title} className="rounded-2xl border bg-card p-6">
 <h3 className="text-lg font-semibold">{c.title}</h3>
 <p className="mt-2 text-muted-foreground">{c.body}</p>
 </div>
 ))}
 </div>
 </Section>

 {/* B2B diligence quick links */}
 <Section className="bg-card">
 <SectionHeader
 eyebrow="B2B diligence"
 title="Compliance, social value and case studies"
 description="Full pages with accreditation status, social-value reporting and project evidence for framework buyers."
 />
 <div className="grid md:grid-cols-3 gap-5">
 {(
 [
 {
 href: COMMERCIAL_URLS.compliance,
 title: COMMERCIAL_LINK_LABELS.compliance,
 body: "Held accreditations and insurances with status and renewal dates.",
 },
 {
 href: COMMERCIAL_URLS.socialValue,
 title: COMMERCIAL_LINK_LABELS.socialValue,
 body: "Local jobs, apprenticeships, supply chain and community benefit, agreed up front and reported on completion.",
 },
 {
 href: COMMERCIAL_URLS.caseStudies,
 title: COMMERCIAL_LINK_LABELS.caseStudies,
 body: "Commercial and framework projects with scope, risks managed and outcomes.",
 },
 ] as const
 ).map((item) => (
 <a
 key={item.href}
 href={item.href}
 className="rounded-2xl border bg-background p-6 flex flex-col hover:border-accent/40 transition-colors"
 >
 <h3 className="text-lg font-semibold text-primary">{item.title}</h3>
 <p className="mt-2 text-sm text-muted-foreground flex-1">{item.body}</p>
 <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
 View full page
 <ArrowRight className="h-3.5 w-3.5" aria-hidden />
 </p>
 </a>
 ))}
 </div>
 </Section>

 {/* D) Compliance & accreditations table */}
 <Section className="bg-card">
 <SectionHeader
 align="center"
 eyebrow="Accreditations & insurance"
 title="Pre-qualified for your supply chain"
 description="Logos, certificates and renewal dates collated to reduce buyer diligence friction."
 />
 <AccreditationStrip />

 <div className="mt-8 rounded-2xl border bg-background overflow-hidden">
 <table className="w-full text-sm">
 <thead className="bg-secondary/40 text-primary">
 <tr>
 <th className="text-left font-semibold px-4 py-3">Accreditation / Cover</th>
 <th className="text-left font-semibold px-4 py-3">Scope / Cover</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {[
 { name: "MCS", scope: "Solar PV, Air Source Heat Pumps" },
 { name: "TrustMark", scope: "Retrofit (PAS 2030)" },
 { name: "PAS 2030", scope: "Insulation & ventilation retrofit" },
 { name: "Gas Safe", scope: "Boilers, controls, LGSR / CP12" },
 { name: "Public / Product Liability", scope: "£5,000,000 cover" },
 { name: "Employers' Liability", scope: "£10,000,000 cover" },
 { name: "Professional Indemnity", scope: "£2,000,000 cover" },
 { name: "Insurance-Backed Guarantee", scope: "Workmanship cover on installed measures" },
 ].map((row) => (
 <tr key={row.name}>
 <td className="px-4 py-3 font-semibold text-primary">{row.name}</td>
 <td className="px-4 py-3 text-muted-foreground">{row.scope}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>

  <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
  <a
  href="#enquiry"
  className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
  >
  Request Pre-Qualification Pack
  <ArrowRight className="h-4 w-4" aria-hidden />
  </a>
  </div>
 <p className="mt-6 text-center">
 <a
 href={COMMERCIAL_URLS.compliance}
 className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accent/80 transition-colors"
 >
 View full compliance &amp; accreditations
 <ArrowRight className="h-4 w-4" aria-hidden />
 </a>
 </p>
 </Section>

 {/* D) Social value band */}
 <Section>
 <SectionHeader
 eyebrow="Social value"
 title="Local jobs, apprenticeships and community benefit"
 description="Public buyers actively look for social-value evidence. We track and report our contribution as part of programme delivery."
 />
 <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
 {[
 { icon: Users, title: "Local jobs", body: "Skilled engineering, installation and surveying roles based in Wales." },
 { icon: Award, title: "Apprenticeships", body: "In-house apprenticeships across retrofit, gas and electrical disciplines." },
 { icon: Briefcase, title: "Local supply chain", body: "Preferred use of Welsh suppliers and sub-contractors where competitive." },
 { icon: HandHeart, title: "Community benefit", body: "Targeted contributions to community projects in the areas we work." },
 ].map((c) => {
 const Icon = c.icon;
 return (
 <div key={c.title} className="rounded-2xl border bg-card p-6">
 <div className="rounded-xl bg-accent/10 text-accent p-3 w-fit">
 <Icon className="h-6 w-6" aria-hidden />
 </div>
 <h3 className="mt-3 text-lg font-semibold text-primary">{c.title}</h3>
 <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
 </div>
 );
 })}
 </div>
 <p className="mt-8 text-center">
 <a
 href={COMMERCIAL_URLS.socialValue}
 className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accent/80 transition-colors"
 >
 View social value reporting
 <ArrowRight className="h-4 w-4" aria-hidden />
 </a>
 </p>
 </Section>

 {/* D) Case studies placeholder */}
 <Section className="bg-card">
 <SectionHeader
 eyebrow="Case studies"
 title="Selected programme outcomes"
 description="Scope, risks managed and outcome. Photo evidence and references available on request."
 />
 <div className="grid md:grid-cols-3 gap-5">
 {[
 {
 scope: "HA portfolio damp & mould response",
 risks: "Tenant disrepair escalation, WHQS hazard rule readiness.",
 outcome: "Diagnostics, remediation and ventilation delivered to agreed SLA with full reporting.",
 },
 {
 scope: "LA retrofit programme (loft + IWI)",
 risks: "EPC uplift targets, ventilation balance after airtightness changes.",
 outcome: "PAS 2030 / TrustMark delivery with insurance-backed guarantee and ventilation coordinated alongside fabric works.",
 },
 {
 scope: "Insurer-led structural & damp claim",
 risks: "Single-property scope, evidenced cause and remedy, sign-off documentation.",
 outcome: "Borescope evidence, remediation and post-works verification handed back with photographic records.",
 },
 ].map((c) => (
 <div key={c.scope} className="rounded-2xl border bg-background p-6 flex flex-col">
 <h3 className="text-base font-semibold text-primary">{c.scope}</h3>
 <p className="mt-2 text-sm text-muted-foreground"><span className="font-semibold text-primary">Risks managed:</span> {c.risks}</p>
 <p className="mt-2 text-sm text-muted-foreground"><span className="font-semibold text-primary">Outcome:</span> {c.outcome}</p>
 </div>
 ))}
 </div>
 <p className="mt-8 text-center">
 <a
 href={COMMERCIAL_URLS.caseStudies}
 className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accent/80 transition-colors"
 >
 View case studies
 <ArrowRight className="h-4 w-4" aria-hidden />
 </a>
 </p>
 </Section>

 {/* Enquiry */}
 <Section>
 <span id="enquiry" aria-hidden className="block scroll-mt-24" />
 <div className="grid lg:grid-cols-2 gap-10 items-center">
 <div>
 <SectionHeader title="Tell us about your scheme" description="We'll come back within one working day." />
 <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-3 py-1.5 text-xs font-semibold text-primary">
 <ShieldCheck className="h-3.5 w-3.5 text-accent" aria-hidden />
 Insurance-backed and manufacturer-backed guarantees as standard
 </div>
 </div>
 <EnquiryForm
 formType="contact"
 variant="contact"
 sourcePage="/for-trade"
 title="B2B enquiry"
 submitLabel="Send enquiry"
 />
 </div>
 </Section>

 <CTABand title="Let's talk about your programme." variant="primary" />
 </>
 );
}
